/**
 * Личный кабинет и управление учётными записями (owner-панель, Б5.4). Экран отвечает на
 * три вопроса: кто заведён, когда он последний раз входил и что он делал.
 *
 * ХЭША ПАРОЛЯ В ЭТИХ СХЕМАХ НЕТ И НЕ БУДЕТ. Он не «скрыт» — его нет в форме ответа,
 * поэтому забыть его убрать негде: `strictObject` отвергнет ответ, в котором он появится.
 *
 * УДАЛЕНИЯ УЧЁТНОЙ ЗАПИСИ ТОЖЕ НЕТ, и это решение, а не упущение. На `app_user.id`
 * ссылаются `decision.actor_user_id`, `approval_key.issued_by_user_id`,
 * `draft_quality_mark.user_id`, `ui_session.user_id` и `login_attempt.user_id`, а строка
 * аудита несёт актора текстом `human:<id>`. Удалить строку — значит либо порвать эти
 * ссылки, либо оставить журнал, в котором решения приняты неизвестно кем; журнал живёт не
 * менее шести месяцев (NFR-2) и обязан оставаться читаемым. Поэтому уход человека — это
 * `is_active = 0`: вход запрещён, сессии отозваны, история цела.
 */

import * as z from 'zod';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/** Роль — CHECK `app_user.role`. Список закрыт: см. `SessionUserSchema` в `ui.ts`. */
export const ACCOUNT_ROLES = ['approver', 'admin'] as const;
export const AccountRoleSchema = z.enum(ACCOUNT_ROLES);
export type AccountRole = z.output<typeof AccountRoleSchema>;

/**
 * Логин: строчная латиница, цифры, точка, дефис, подчёркивание, `@` и `+` — то есть и
 * рабочая почта (`i.baikov@strategai.ru`), которой люди и заведены в seed. Он попадает в
 * `актор` строк журнала и в подписываемый payload ключа (`approver`), поэтому пробелов и
 * юникода в нём быть не может — иначе одна и та же учётная запись выглядела бы по-разному
 * в разных местах в зависимости от нормализации.
 *
 * Алфавит — строгое подмножество `APPROVER_RE` (`approval-key/limits.ts`): всё, что
 * проходит форму, обязан принять и демон подписи; тест рядом это утверждает.
 */
export const ACCOUNT_LOGIN_RE = /^[a-z0-9][a-z0-9._@+-]{1,63}$/;

/** Совпадает с `PASSWORD_MIN_LENGTH` бэкенда; расхождение дало бы 400 вместо подсказки в форме. */
export const ACCOUNT_PASSWORD_MIN = 12;
export const ACCOUNT_PASSWORD_MAX = 1024;

const Password = z.string().min(ACCOUNT_PASSWORD_MIN).max(ACCOUNT_PASSWORD_MAX);

/**
 * Учётная запись глазами экрана.
 *
 * `last_login_at` — момент ПОСЛЕДНЕГО УДАЧНОГО входа (`login_attempt.ok = 1`), а не
 * последней активности сессии: вопрос «кто ещё пользуется системой» отвечается входом,
 * а `ui_session.last_seen_at_ms` двигается фоновыми запросами открытой вкладки.
 *
 * `password_set_at` отвечает на «пароль вообще задан?»: у заведённого без пароля человека
 * он `null`, и вход ему запрещён так же, как заблокированному, — но это разные состояния,
 * и экран обязан их различать.
 */
export const AccountSchema = z.strictObject({
  user_id: Uuid,
  login: z.string().min(1),
  display_name: z.string(),
  role: AccountRoleSchema,
  is_active: z.boolean(),
  created_at: IsoUtcMs,
  password_set_at: IsoUtcMs.nullable(),
  last_login_at: IsoUtcMs.nullable(),
});
export type Account = z.output<typeof AccountSchema>;

export const AccountsResponseSchema = z.strictObject({
  accounts: z.array(AccountSchema),
  /** Может ли смотрящий менять учётные записи. Роль экран знает и сам, но право — не роль. */
  can_manage: z.boolean(),
});
export type AccountsResponse = z.output<typeof AccountsResponseSchema>;

export const CreateAccountBodySchema = z.strictObject({
  login: z.string().regex(ACCOUNT_LOGIN_RE),
  display_name: z.string().min(1).max(128),
  role: AccountRoleSchema,
  /**
   * Пароль задаётся сразу. Заводить человека без пароля через интерфейс незачем: путь
   * «завели, а войти нельзя» уже есть у CLI (`npm run user:passwd`) и породил бы вопрос
   * «почему не пускает» вместо учётной записи.
   */
  password: Password,
});
export type CreateAccountBody = z.output<typeof CreateAccountBodySchema>;

/**
 * Смена пароля. `current_password` обязателен, когда человек меняет СВОЙ пароль, и
 * запрещён, когда администратор назначает чужой: в первом случае это подтверждение
 * личности сидящего за клавиатурой, во втором — поле, которого спрашивающий не знает
 * и знать не должен. Какая из двух веток действует, решает бэкенд по сессии, а не тело.
 */
export const SetPasswordBodySchema = z.strictObject({
  current_password: z.string().min(1).max(ACCOUNT_PASSWORD_MAX).nullable(),
  new_password: Password,
});
export type SetPasswordBody = z.output<typeof SetPasswordBodySchema>;

export const SetAccountActiveBodySchema = z.strictObject({
  active: z.boolean(),
});
export type SetAccountActiveBody = z.output<typeof SetAccountActiveBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Журнал действий человека

/**
 * Строка журнала. Содержимое `payload_json` наружу НЕ едет целиком: там лежат отпечатки
 * подписи, тексты подсказок и прочее, что к вопросу «что человек делал» отношения не
 * имеет. Едут пять величин, из которых строка читается словами: что произошло, с каким
 * постом, какой версией и между какими состояниями.
 */
export const AccountActivityEntrySchema = z.strictObject({
  /** `audit_event.seq` — он же курсор постраничности: монотонный и неповторяемый. */
  seq: z.int().positive(),
  occurred_at: IsoUtcMs,
  event_type: z.string().min(1),
  brand: z.string().nullable(),
  draft_id: Uuid.nullable(),
  version_no: z.int().positive().nullable(),
  /** Заполнены только у `draft.status_changed`; у прочих событий перехода нет. */
  from_status: z.string().nullable(),
  to_status: z.string().nullable(),
});
export type AccountActivityEntry = z.output<typeof AccountActivityEntrySchema>;

/** Потолок страницы журнала. Экран показывает список, а не выгружает полгода. */
export const ACCOUNT_ACTIVITY_MAX_LIMIT = 200;
export const ACCOUNT_ACTIVITY_DEFAULT_LIMIT = 50;

/**
 * Постраничность идёт ПО `seq` ВНИЗ, а не по смещению: журнал дописывается, пока человек
 * листает, и `OFFSET` показывал бы одну и ту же строку дважды. `next_before_seq` пуст,
 * когда страница последняя.
 */
export const AccountActivityResponseSchema = z.strictObject({
  user_id: Uuid,
  entries: z.array(AccountActivityEntrySchema),
  next_before_seq: z.int().positive().nullable(),
});
export type AccountActivityResponse = z.output<typeof AccountActivityResponseSchema>;
